import { AxiosError } from 'axios'
import type { AxiosResponse } from 'axios'

import { ErrorType } from '#web-oura-tools/types/errors'
import type {
  ApiErrorResponse,
  ApiErrorResponseType,
  ApiGWErrorResponse,
  ApiGWErrorResponseType,
  ErrorNotification,
  FormValidationErrorResponse,
  FormValidationErrorResponseType,
  RequestError,
} from '#web-oura-tools/types/errors'

export function isApiErrorResponse(x: unknown): x is ApiErrorResponseType {
  if (x === undefined || x === null) {
    return false
  }

  const testType = x as ApiErrorResponseType
  return testType.message !== undefined && testType.code !== undefined
}

export function isFormValidationError(x: unknown): x is FormValidationErrorResponse {
  if (x === undefined || x === null) {
    return false
  }

  const testType = x as FormValidationErrorResponseType
  return testType.success !== undefined && testType.problems !== undefined
}

export function isApiGWErrorResponse(x: unknown): x is ApiGWErrorResponseType {
  if (x === undefined || x === null) {
    return false
  }

  const testType = x as ApiGWErrorResponseType
  return testType.message !== undefined
}

/**
 * Detects a Twingate edge block.
 *
 * When the client isn't connected to Twingate, requests to the internal API are rejected at the
 * edge with a 403 before they ever reach the backend. A genuine permissions 403 comes from the
 * backend and always carries an X-Outo-Request-Id header (exposed as `requestId`); a Twingate
 * block never does. So a 403 without a request id is treated as a Twingate problem.
 */
export function isTwingateBlock(statusCode: number | null | undefined, requestId: string | null | undefined): boolean {
  const hasRequestId = !!requestId && requestId.toLowerCase() !== 'n/a'
  return statusCode === 403 && !hasRequestId
}

/**
 * Handle request error
 *
 * Improvement ideas:
 * - Handle more error cases (request cancelled, timed out, network error etc.)
 */
export async function handleRequestError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: AxiosError<any | ApiErrorResponse | FormValidationErrorResponse | ApiGWErrorResponse>,
  source: string,
): Promise<RequestError> {
  const statusCode = error?.response?.status ?? null
  const timestamp = Date.now()
  const stackTrace = new Error().stack ?? 'no stack-trace available'
  const responseData =
    error.response?.data instanceof Blob ? await parseBlob(error.response.data) : error.response?.data

  const details = {
    statusCode: statusCode,
    responseData: responseData ?? null,
    url: error?.response?.request?.responseURL ?? error?.config?.url ?? null,
    requestId: parseRequestId(error.response),
    sentryTrace: parseSentryTrace(error.response),
    stackTrace: stackTrace,
    originalError: error,
  }

  if (error?.code === AxiosError.ERR_CANCELED) {
    return {
      userMessage: 'Request was cancelled',
      verboseMessage: null,
      type: ErrorType.REQUEST_CANCELLED,
      source: source,
      timestamp: timestamp,
      details: details,
      formValidationProblems: [],
    }
  } else if (error?.code === AxiosError.ECONNABORTED) {
    return {
      userMessage: 'Request was cancelled because it took too long to get a response from the server.',
      verboseMessage: error?.message ?? null,
      type: ErrorType.AXIOS_ECONNABORTED,
      source: source,
      timestamp: timestamp,
      details: details,
      formValidationProblems: [],
    }
  } else if (error?.message === 'Network Error') {
    /**
     * Based on axios-mock-adapter this is how we can determine a network error.
     * Source: https://github.com/ctimmerm/axios-mock-adapter/pull/44/files
     * What causes this is possibly a CORS misconfiguration. Source: https://github.com/axios/axios/issues/4420
     * Reason for this error being so generic is that Axios is just a wrapper for XMLHttpRequest (XHR) which doesn't
     * give any additional details about the error
     */
    return {
      userMessage: 'Network error.',
      verboseMessage: 'Probably a CORS issue.',
      type: ErrorType.AXIOS_ERR_NETWORK,
      source: source,
      timestamp: timestamp,
      details: details,
      formValidationProblems: [],
    }
  }

  if (statusCode && statusCode >= 400 && statusCode <= 499) {
    // Every 401 is an auth failure, whatever the body looks like. Gateways, the backend and the
    // Twingate edge all phrase it differently, and some send no structured body at all, so keying
    // off the response shape here would let real 401s fall through to a generic "client error".
    if (statusCode === 401) {
      return {
        userMessage: 'Unauthorized',
        verboseMessage: null,
        type: ErrorType.UNAUTHORIZED,
        source: source,
        timestamp: timestamp,
        details: details,
        formValidationProblems: [],
      }
    }

    // Require both signals before assuming Twingate: no backend request id AND no structured API
    // error body. A genuine permissions 403 carries an ApiErrorResponse, so it keeps its real
    // message even if (some endpoint) omits the X-Outo-Request-Id header.
    if (isTwingateBlock(statusCode, details.requestId) && !isApiErrorResponse(responseData)) {
      return {
        userMessage: 'Could not reach the service. Check that you are connected to Twingate.',
        verboseMessage: null,
        type: ErrorType.TWINGATE_BLOCKED,
        source: source,
        timestamp: timestamp,
        details: details,
        formValidationProblems: [],
      }
    } else if (isApiErrorResponse(responseData)) {
      return {
        userMessage: responseData.message ?? null,
        verboseMessage: null,
        type: ErrorType.CLIENT_ERROR,
        source: source,
        timestamp: timestamp,
        details: details,
        formValidationProblems: [],
      }
    } else if (isFormValidationError(responseData)) {
      return {
        userMessage: 'Form validation error',
        verboseMessage: null,
        type: ErrorType.FORM_VALIDATION_ERROR,
        source: source,
        timestamp: timestamp,
        details: details,
        formValidationProblems: responseData.problems,
      }
    } else if (isApiGWErrorResponse(responseData) && statusCode == 413) {
      return {
        userMessage: 'Request entity too large',
        verboseMessage: null,
        type: ErrorType.PAYLOAD_TOO_LARGE,
        source: source,
        timestamp: timestamp,
        details: details,
        formValidationProblems: [],
      }
    } else {
      return {
        userMessage: 'Unknown error',
        verboseMessage: null,
        type: ErrorType.UNEXPECTED_CLIENT_ERROR,
        source: source,
        timestamp: timestamp,
        details: details,
        formValidationProblems: [],
      }
    }
  }

  if (statusCode && statusCode >= 500 && statusCode <= 599) {
    /*
     * Server error (500, 503, 504) are currently handled by Axios interceptor
     * and global notification is raised from those. We still should probably return this error
     * so we can show in the UI where the error actually happened.
     */
    return {
      userMessage: responseData?.message ?? 'Unknown server error',
      verboseMessage: null,
      type: ErrorType.UNEXPECTED_SERVER_ERROR,
      source: source,
      timestamp: timestamp,
      details: details,
      formValidationProblems: [],
    }
  }
  // Unexpected error which is not client or server error
  return {
    userMessage: 'Unknown error',
    verboseMessage: null,
    type: ErrorType.UNEXPECTED_ERROR,
    source: source,
    timestamp: timestamp,
    details: details,
    formValidationProblems: [],
  }
}

async function parseBlob(blob: Blob) {
  try {
    if (blob.type === 'application/json') {
      const errorText = await blob.text()
      return JSON.parse(errorText)
    } else {
      return null
    }
  } catch (e) {
    console.error('Failed to parse error blob', e)
    return null
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseRequestId(response: AxiosResponse<any, any> | undefined) {
  return parseHeader('X-Outo-Request-Id', response)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseSentryTrace(response: AxiosResponse<any, any> | undefined): string {
  return parseHeader('Sentry-Trace', response)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseHeader(header: string, response: AxiosResponse<any, any> | undefined): string {
  if (response) {
    return response.headers[header.toLowerCase()] ?? 'n/a'
  } else {
    return 'n/a'
  }
}

export function toNotifications(errors: Array<RequestError | null>): ErrorNotification[] {
  return errors.filter((error) => error !== null).map((error) => toNotification(error))
}

export function toNotification(error: RequestError): ErrorNotification {
  return {
    message: error.userMessage ? error.userMessage : 'error message was not provided',
    timestamp: error.timestamp,
    details: {
      'Status Code': error.details.statusCode ? error.details.statusCode.toString() : 'N/A',
      'Request ID': error.details.requestId,
      'Sentry Trace ID': error.details.sentryTrace,
      'Request Source': error.source,
      'Error Reason': error.verboseMessage ?? 'No reason available',
      'URL': error.details.url ?? 'No URL available',
    },
    severity: 'error',
  }
}
